import { supabase } from '../../../integrations/supabase/client';

export interface Agent {
  id: string;
  user_id: string;
  nombre: string;
  descripcion?: string | null;
  plan?: string | null;
  rol?: string | null;
  objetivo?: string | null;
  contraindicacion?: string | null;
  notas?: string | null;
  max_mensajes?: number | null;
  tiempo_agrupacion?: number | null;
  instrucciones_kb?: string | null;
  instruccion?: string | null;
  active_workflow_id?: string | null;
  is_active: boolean;
  created_at: string | null;
  updated_at: string | null;
}

export interface AgentChannel {
  id: string;
  agent_id: string;
  numero_telefono: string;
  nombre_instancia?: string | null;
  is_primary: boolean;
  created_at: string | null;
}

export const agentsApi = {
  // ==================== GESTIÓN DE AGENTES ====================

  async createAgent(nombre: string, descripcion?: string): Promise<Agent> {
    console.log('[agentsApi] 🎯 Creando agente:', { nombre, descripcion });

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Usuario no autenticado');

    const { data, error } = await supabase
      .from('agents')
      .insert({
        user_id: user.id,
        nombre,
        descripcion,
        is_active: true,
      })
      .select()
      .single();

    if (error) {
      console.error('[agentsApi] ❌ Error creando agente:', error);
      throw error;
    }

    console.log('[agentsApi] ✅ Agente creado:', data);
    return {
      ...data,
      is_active: data.is_active ?? true,
    };
  },

  async listAgents(): Promise<Agent[]> {
    console.log('[agentsApi] 🔄 Listando agentes...');

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Usuario no autenticado');

    const { data, error } = await supabase
      .from('agents')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('[agentsApi] ❌ Error listando agentes:', error);
      throw error;
    }

    console.log('[agentsApi] ✅ Agentes cargados:', data?.length || 0);
    return (data || []).map(agent => ({
      ...agent,
      is_active: agent.is_active ?? true,
    }));
  },

  async getAgent(agentId: string): Promise<Agent | null> {
    console.log('[agentsApi] 🔍 Obteniendo agente:', agentId);

    const { data, error } = await supabase
      .from('agents')
      .select('*')
      .eq('id', agentId)
      .maybeSingle();

    if (error) {
      console.error('[agentsApi] ❌ Error obteniendo agente:', error);
      throw error;
    }

    console.log('[agentsApi] ✅ Agente obtenido:', data);
    return data ? {
      ...data,
      is_active: data.is_active ?? true,
    } : null;
  },

  async updateAgent(agentId: string, updates: Partial<Agent>): Promise<Agent> {
    console.log('[agentsApi] 🔄 Actualizando agente:', agentId, updates);

    const { data, error } = await supabase
      .from('agents')
      .update(updates)
      .eq('id', agentId)
      .select()
      .single();

    if (error) {
      console.error('[agentsApi] ❌ Error actualizando agente:', error);
      throw error;
    }

    console.log('[agentsApi] ✅ Agente actualizado:', data);
    return {
      ...data,
      is_active: data.is_active ?? true,
    };
  },

  async deleteAgent(agentId: string): Promise<void> {
    console.log('[agentsApi] 🗑️ Eliminando agente:', agentId);

    const { error } = await supabase
      .from('agents')
      .delete()
      .eq('id', agentId);

    if (error) {
      console.error('[agentsApi] ❌ Error eliminando agente:', error);
      throw error;
    }

    console.log('[agentsApi] ✅ Agente eliminado');
  },

  // ==================== GESTIÓN DE CANALES ====================

  async getAgentChannels(agentId: string): Promise<AgentChannel[]> {
    console.log('[agentsApi] 🔍 Obteniendo canales del agente:', agentId);

    const { data, error } = await supabase
      .from('agent_channels')
      .select('*')
      .eq('agent_id', agentId)
      .order('is_primary', { ascending: false });

    if (error) {
      console.error('[agentsApi] ❌ Error obteniendo canales:', error);
      throw error;
    }

    console.log('[agentsApi] ✅ Canales obtenidos:', data?.length || 0);
    return (data || []).map(channel => ({
      ...channel,
      is_primary: channel.is_primary ?? false,
    }));
  },

  async connectChannel(
    agentId: string,
    numeroTelefono: string,
    nombreInstancia?: string,
    isPrimary: boolean = false
  ): Promise<AgentChannel> {
    console.log('[agentsApi] 🔗 Conectando canal al agente:', { agentId, numeroTelefono, isPrimary });

    const { data, error } = await supabase
      .from('agent_channels')
      .insert({
        agent_id: agentId,
        numero_telefono: numeroTelefono,
        nombre_instancia: nombreInstancia,
        is_primary: isPrimary,
      })
      .select()
      .single();

    if (error) {
      console.error('[agentsApi] ❌ Error conectando canal:', error);
      throw error;
    }

    console.log('[agentsApi] ✅ Canal conectado:', data);
    return {
      ...data,
      is_primary: data.is_primary ?? false,
    };
  },

  async disconnectChannel(channelId: string): Promise<void> {
    console.log('[agentsApi] 🔌 Desconectando canal:', channelId);

    const { error } = await supabase
      .from('agent_channels')
      .delete()
      .eq('id', channelId);

    if (error) {
      console.error('[agentsApi] ❌ Error desconectando canal:', error);
      throw error;
    }

    console.log('[agentsApi] ✅ Canal desconectado');
  },

  async setPrimaryChannel(agentId: string, channelId: string): Promise<void> {
    console.log('[agentsApi] ⭐ Marcando canal como principal:', { agentId, channelId });

    // Primero quitar is_primary de todos los canales del agente
    await supabase
      .from('agent_channels')
      .update({ is_primary: false })
      .eq('agent_id', agentId);

    // Luego marcar el canal seleccionado como principal
    const { error } = await supabase
      .from('agent_channels')
      .update({ is_primary: true })
      .eq('id', channelId);

    if (error) {
      console.error('[agentsApi] ❌ Error marcando canal principal:', error);
      throw error;
    }

    console.log('[agentsApi] ✅ Canal principal actualizado');
  },

  // ==================== GESTIÓN DE WORKFLOW ====================

  async setActiveWorkflow(agentId: string, workflowId: string): Promise<void> {
    console.log('[agentsApi] 🔄 Asignando workflow activo:', { agentId, workflowId });

    const { error } = await supabase
      .from('agents')
      .update({ active_workflow_id: workflowId })
      .eq('id', agentId);

    if (error) {
      console.error('[agentsApi] ❌ Error asignando workflow:', error);
      throw error;
    }

    console.log('[agentsApi] ✅ Workflow activo asignado');
  },

  async clearActiveWorkflow(agentId: string): Promise<void> {
    console.log('[agentsApi] 🔄 Removiendo workflow activo:', agentId);

    const { error } = await supabase
      .from('agents')
      .update({ active_workflow_id: null })
      .eq('id', agentId);

    if (error) {
      console.error('[agentsApi] ❌ Error removiendo workflow:', error);
      throw error;
    }

    console.log('[agentsApi] ✅ Workflow activo removido');
  },
};
